import React from 'react';
import { useQuery } from 'react-query';
import { 
  CalendarIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  PhoneIcon, 
  CurrencyDollarIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import { statusLabels, getStatusColor } from '../config/statusColors';

const StatusLogsSection = ({ projectId, projectFiles }) => {
  const { user } = useAuthStore();
  
  // 상태 변경 로그 조회
  const { data: statusLogs, isLoading, error } = useQuery(
    ['statusLogs', projectId],
    () => api.get(`/projects/${projectId}/status-logs`).then(res => res.data),
    {
      enabled: !!projectId,
      refetchOnWindowFocus: true, // 창 포커스 시 새로고침
      refetchInterval: 5000, // 5초마다 자동 새로고침
      onError: (err) => {
        console.error('Failed to fetch status logs:', err);
      }
    }
  );

  console.log('StatusLogs:', { projectId, statusLogs, isLoading, error });

  // 로그 타입별 아이콘과 색상
  const getLogTypeInfo = (logType) => {
    switch(logType) {
      case 'visit_scheduled':
        return { icon: CalendarIcon, color: 'text-blue-600', bgColor: 'bg-blue-100' };
      case 'quote_review':
        return { icon: CurrencyDollarIcon, color: 'text-green-600', bgColor: 'bg-green-100' };
      case 'simple_consultation':
        return { icon: PhoneIcon, color: 'text-green-600', bgColor: 'bg-green-100' };
      case 'immediate_cancel':
        return { icon: XCircleIcon, color: 'text-red-600', bgColor: 'bg-red-100' };
      case 'contract_signed':
        return { icon: DocumentTextIcon, color: 'text-indigo-600', bgColor: 'bg-indigo-100' };
      default:
        return { icon: InformationCircleIcon, color: 'text-gray-600', bgColor: 'bg-gray-100' };
    }
  };

  // 상태별 아이콘
  const getStatusIcon = (status) => {
    switch(status) {
      case 'received':
        return InformationCircleIcon;
      case 'review':
        return ClipboardDocumentCheckIcon;
      case 'contract':
        return DocumentTextIcon;
      case 'completed':
        return CheckCircleIcon;
      case 'cancelled':
        return XCircleIcon;
      default:
        return InformationCircleIcon;
    }
  };

  // 로그가 없어도 섹션은 표시
  const hasLogs = statusLogs && statusLogs.length > 0;

  return (
    <div>
      <h3 className="text-lg font-bold text-gray-900 mb-3">프로젝트 진행 상황</h3>
      
      {!hasLogs ? (
        <div className="text-center py-8 text-gray-500">
          <InformationCircleIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">진행 기록이 없습니다</p>
          <p className="text-xs mt-1">상태 변경 시 여기에 기록됩니다</p>
        </div>
      ) : (
        <div className="space-y-3">
          {statusLogs.map((log, index) => {
          const logInfo = getLogTypeInfo(log.log_type);
          const StatusIcon = getStatusIcon(log.new_status);
          const isLatest = index === 0;
          
          return (
            <div 
              key={log.id} 
              className={`relative ${index !== statusLogs.length - 1 ? 'pb-3' : ''}`}
            >
              {/* 연결선 */}
              {index !== statusLogs.length - 1 && (
                <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200"></div>
              )}
              
              {/* 로그 내용 */}
              <div className="flex gap-3">
                {/* 상태 아이콘 */}
                <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  isLatest ? getStatusColor(log.new_status, 'primary') : 'bg-gray-200'
                }`}>
                  <StatusIcon className={`h-4 w-4 ${
                    isLatest ? 'text-white' : 'text-gray-500'
                  }`} />
                </div>
                
                {/* 내용 */}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* 상태 변경 */}
                      <p className="text-sm font-medium text-gray-900">
                        {statusLabels[log.new_status]} 단계
                        {log.old_status && (
                          <span className="text-gray-500 font-normal">
                            {' '}(이전: {statusLabels[log.old_status]})
                          </span>
                        )}
                      </p>
                      
                      {/* 로그 메시지 */}
                      {log.log_message && (
                        <div className={`mt-1 flex items-start gap-2 p-2 rounded-lg ${logInfo.bgColor}`}>
                          <logInfo.icon className={`h-4 w-4 ${logInfo.color} flex-shrink-0 mt-0.5`} />
                          <div className="text-sm text-gray-700">
                            <p>{log.log_message}</p>
                            {log.user_memo && (
                              <p className="mt-1 text-gray-600">
                                📝 메모: {log.user_memo}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* 계약서 파일 표시 */}
                      {log.new_status === 'contract' && projectFiles && (
                        (() => {
                          const contractFiles = projectFiles.filter(file => file.category === 'contract');
                          if (contractFiles.length > 0) {
                            return (
                              <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                                <p className="text-xs font-medium text-gray-600 mb-1">📎 계약서 첨부파일</p>
                                <div className="space-y-1">
                                  {contractFiles.map((file, idx) => {
                                    const fileName = file.file_name || file.file_path;
                                    const displayName = fileName.length > 30 
                                      ? `${fileName.slice(0, 20)}...${fileName.slice(-7)}` 
                                      : fileName;
                                    
                                    return (
                                      <a
                                        key={idx}
                                        href={`${process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : ''}/uploads/projects/${file.file_path}`}
                                        download={fileName}
                                        className="text-xs text-blue-600 hover:text-blue-700 hover:underline block truncate"
                                        title={fileName}
                                      >
                                        {displayName}
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()
                      )}
                      
                      {/* 내부 메모 (관리자/소장만) */}
                      {log.internal_memo && (user.user_type === 'admin' || user.user_type === 'contractor') && (
                        <div className="mt-1 p-2 bg-orange-50 rounded-lg border border-orange-200">
                          <p className="text-xs text-orange-800">
                            <span className="font-medium">내부 메모:</span> {log.internal_memo}
                          </p>
                        </div>
                      )}
                      
                      {/* 작성자와 시간 */}
                      <p className="text-xs text-gray-500 mt-1">
                        {log.user_name} • {log.formatted_time}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
};

export default StatusLogsSection;